import React, { useEffect, useRef, useState } from 'react';
import { Animated, Insets, Pressable, StyleSheet } from 'react-native';

import Slash from '@/assets/icons/slash';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';
import { nativeDriverValue } from '@/utils/constants-platform-specific';

export const animationConfig = {
    useNativeDriver: nativeDriverValue,
    mass: 1,
    damping: 25,
    stiffness: 250,
    overshootClamping: false,
    restSpeedThreshold: 0.001,
    restDisplacementThreshold: 0.001,
};

export type SwitchProps = {
    val: boolean;
    handleOnPress: () => void;
    disabled?: boolean;
    warning?: boolean;
    activeTrackColor?: string;
    inactiveTrackColor?: string;
    warningColor?: string;
    testID?: string;
    onIcon?: React.ReactNode;
    offIcon?: React.ReactNode;
    editable?: boolean;
    hitSlop?: Insets | number;
};

export const Switch = (props: SwitchProps) => {
    const {
        handleOnPress,
        activeTrackColor = ds.colorValue.background.success.strong,
        inactiveTrackColor = ds.colorValue.background.neutral.subtle,
        warningColor = ds.colorValue.background.warning.strong,
        val,
        disabled,
        warning,
        testID,
        onIcon,
        offIcon,
        editable = true,
        hitSlop,
    } = props;

    const switchTranslate = useRef(new Animated.Value(Number(val))).current;
    const [statusIcon, setStatusIcon] = useState(val ? onIcon : offIcon);

    // Used to avoid running the animation/effect on the initial mount
    const hasMountedRef = useRef(false);

    useEffect(() => {
        // Skip the initial render: everything is already in the correct state
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        Animated.spring(switchTranslate, {
            toValue: Number(val),
            ...animationConfig,
        }).start(() => {
            setStatusIcon(val ? onIcon : offIcon);
        });
    }, [val, onIcon, offIcon, switchTranslate]);

    const trackBackgroundColor = {
        backgroundColor: switchTranslate.interpolate({
            inputRange: [0, 0.75, 1],
            outputRange: [inactiveTrackColor, inactiveTrackColor, activeTrackColor],
        }),
    };

    const isWarningStyle = warning && Boolean(val) && !disabled;
    const showStatusIcons = !!onIcon && !!offIcon;

    return (
        <Pressable
            accessibilityRole="switch"
            disabled={disabled || !editable}
            onPress={handleOnPress}
            // Pass accessibility hints so reassure can properly detect render changes
            accessibilityState={{
                checked: val,
                disabled: disabled || !editable,
            }}
            testID={testID}
            hitSlop={hitSlop}
        >
            <Animated.View
                style={[
                    styles.containerStyle,
                    trackBackgroundColor,
                    isWarningStyle && { backgroundColor: warningColor },
                    disabled && styles.disabledStyle,
                ]}
            >
                <Animated.View
                    style={[
                        styles.circleStyle,
                        disabled && styles.disabledCircleStyle,
                        {
                            transform: [
                                {
                                    translateX: switchTranslate.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [2, 18],
                                        extrapolate: 'clamp',
                                    }),
                                },
                            ],
                        },
                    ]}
                >
                    {showStatusIcons ? statusIcon : null}
                    {isWarningStyle ? <Slash /> : null}
                </Animated.View>
            </Animated.View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    circleStyle: {
        width: 24,
        height: 24,
        backgroundColor: ds.colorValue.background.neutral.inverse,
        borderRadius: 12,
        borderCurve: 'continuous',
        justifyContent: 'center',
        alignItems: 'center',
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowColor: designSystem.colors.neutral900,
        shadowOpacity: 0.25,
        shadowRadius: 2,
        elevation: 1,
    },
    disabledCircleStyle: {
        backgroundColor: designSystem.colors.neutral400,
    },
    disabledStyle: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    containerStyle: {
        width: 44,
        height: 28,
        justifyContent: 'center',
        borderRadius: 100,
        borderCurve: 'continuous',
    },
});
